'use client';

import { useContext } from 'react';
import { PlatformContext } from '../context/PlatformContext';
import { WebDefaults } from '../constants/WebDefaults';
import { ok, err } from '../../../../utils/src/error';
import type { Result, AppError } from '../../../../utils/src/error';
import type { PlatformPrimitives } from '../types/PlatformPrimitives';

/**
 * @group Functions
 * 
 * @summary
 * <blockquote>Hook to access platform primitives from React context.</blockquote>
 * 
 * @example
 * ```typescript
 * const result = usePlatformComponents();
 * result.match({
 *   ok: (primitives) => {
 *     const { View, Svg, Path } = primitives;
 *   },
 *   err: (error) => {
 *     console.error('Failed to get platform components:', error.errorMessage);
 *   }
 * });
 * ```
 * 
 * @returns Result containing platform primitives or an error if used outside PlatformProvider
 */
export const /**
 * @internal
 */
usePlatformComponents = (): Result<PlatformPrimitives, AppError> => {
  const context = useContext(PlatformContext);
  if (!context) {
    return err({
      errorCode: 'CONTEXT_NOT_FOUND',
      errorMessage: 'usePlatformComponents must be used within PlatformProvider'
    });
  }
  return ok(context);
}; 